import { Request } from '@/classes/Request'
import { isFormSubmissionId } from '@/modules/util/formSubmissionId'

/**
 * Link a completed form submission to an assigned fee.
 *
 * DTA-659: `formSubmissionId` is a MongoDB ObjectId string, not a number. It was
 * typed `number` here originally, which is why callers reached for `?? 0` to
 * satisfy the compiler and wrote the literal "0" into the column 7,541 times.
 * Only call this when a form has actually been submitted — there is no
 * "no submission" value to send, and the server rejects anything that is not an
 * ObjectId.
 */
export const setFormSubmissionId = async (
  formSubmissionId: string,
  studentAssignedFeeId: number
): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (!isFormSubmissionId(formSubmissionId)) {
      // Fail loudly rather than sending a placeholder the server will reject —
      // and never silently orphan the fee from its submission.
      reject(
        `Refusing to send an invalid form submission id (${JSON.stringify(formSubmissionId)}) ` +
          `for assigned fee ${studentAssignedFeeId}`
      )
      return
    }

    const req = new Request()
    req
      .post(`/ajax/form/set_form_submission_id`, {
        // Send what was validated. isFormSubmissionId trims before matching, so a
        // padded id passes the check above; sending it raw would store the padding
        // server-side, where an exact-match lookup no longer finds it.
        form_submission_id: formSubmissionId.trim(),
        student_assigned_fee_id: studentAssignedFeeId
      })
      .then(() => {
        resolve()
      })
      .catch(() => {
        reject('Failed to set form submission ID')
      })
  })
}
